import { fromFetch } from 'rxjs/fetch';
import { of, Observable } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AbrExampleByAbn } from './example.snsw.byabn';
import { AbrExampleByName } from './example.snsw.byName';
import { AbrExampleByAcn } from './example.byAcn';

export type abrOAuthService = {
    hasValidAccessToken: () => boolean
    authorizationHeader: () => string | null
};

export const sessionStorageOAuthService: abrOAuthService = {
    hasValidAccessToken: () => !!sessionStorage.getItem('access_token'),
    authorizationHeader: () => `Bearer ${sessionStorage.getItem('access_token')}`
};

export const cookieOAuthService: abrOAuthService = {
    hasValidAccessToken: () => true,
    authorizationHeader: () => null
};

export const mockOAuthService: abrOAuthService = {
    hasValidAccessToken: () => true,
    authorizationHeader: () => null
};

function isNumericString(input) {
    return typeof input === 'string' && !Number.isNaN(input);
}

export type AbrHttpClient = {
    get: (url: string, httpOptions: object) => Observable<any>
}

export const abrHttpClient: AbrHttpClient = {
    get: (url, httpOptions) => {
        const observable = fromFetch(url, {
            method: 'GET',
            headers: httpOptions.headers
        }).pipe(
            switchMap(response => {
                if (response.ok) {
                    // OK return data
                    return response.json();
                } else {
                    // Server is returning a status requiring the client to try something else.
                    return of({ error: true, message: `Error ${response.status}` });
                }
            }),
            catchError(err => {
                // Network or other error, handle appropriately
                console.error(err);
                return of({ error: true, message: err.message });
            })
        );
        //observable.subscribe({next: result => console.log(result), complete: () => console.log('done')})
        return observable;
    }
};

/**
 * this can be used in Storybook or a demo app when not authenticated
 */
export const mockAbrHttpClient: AbrHttpClient = {
    get: (url, httpOptions) => {
        const parsedUrl = new URL(url,window.location.origin);
        if (parsedUrl.pathname.indexOf('abrsearchbyabn') > 0) {
            const abn = parsedUrl.searchParams.get('searchString');
            const result = JSON.parse(JSON.stringify(AbrExampleByAbn));
            const resultBody = result.SearchByABNv201408Response.SearchByABNv201408Result.Response.ResponseBody;
            if (resultBody.ABN.Item.IdentifierValue !== `${abn}`) {
                // if the abn is different to the sample then mock the abn and name in the result
                resultBody.ABN.Item.IdentifierValue = parseInt((`${abn}9999999999999`).substring(0, 11));
                resultBody.Name.Item.OrganisationName = 'Mock Result Name - data from service NSW';
            }
            if (abn === '34241177887') { // for suppressed test data
                resultBody.Name.Item.OrganisationName = '';
                delete resultBody.GoodsAndServicesTax;
                delete resultBody.Name;
                delete resultBody.MainBusinessPhysicalAddress;
                delete resultBody.BusinessName;
                resultBody.EntityType.EntityTypeCode = '';
                resultBody.EntityType.EntityDescription = '';
            }
            return of(result);
        } else if (parsedUrl.pathname.indexOf('abrsearchbyacn') > 0 ) {
            console.warn('mockAbrHttpClient producing result for ', url);
            const acn = parsedUrl.searchParams.get('searchString');
            const result = JSON.parse(JSON.stringify(AbrExampleByAcn));
            const resultBody = result.SearchByASICv201408Response.SearchByASICv201408Result.Response.ResponseBody;
            resultBody.ABN.Item.IdentifierValue = parseInt((`${acn}9999999999999`).substring(0,11));
            resultBody.ASICNumber = `${acn}`;
            resultBody.Name.Item.OrganisationName = `Mock ACN result for ${acn}`;
            return of(result);
        } else if (parsedUrl.pathname.indexOf('abrsearchbyname') > 0) {
            const name = parsedUrl.searchParams.get('name');
            const result = JSON.parse(JSON.stringify(AbrExampleByName));
            const resultOneBody = result.ABRSearchByNameAdvancedSimpleProtocol2017Response
                .ABRSearchByNameAdvancedSimpleProtocol2017Result
                .Response.ResponseBody.SearchResultsRecord.Item[0];
            if (isNumericString(name)) {
                resultOneBody.ABN.Item.IdentifierValue = parseInt((`${name}9999999999999`).substring(0,11));
                resultOneBody.Name.Item.OrganisationName = `Mock Result Name for ${name} - data from service NSW`;
            }
            return of(result);
        } else {
            throw new Error(`some error ; url requested:${url}`);
        }
    }
};
